<template>
  <div class="row q-col-gutter-md">
    <div class="col-12">
      <hr-setting-switch
        v-if="autosave"
        :model-value="enableAvailability"
        :label="enableLabel || $t('availabilityEditor.labelEnable')"
        @update:model-value="enableAvailability = $event"
      />
      <hr-checkbox
        v-else
        v-model="enableAvailability"
        :label="enableLabel || $t('availabilityEditor.labelEnable')"
      />
    </div>
    <template v-if="enableAvailability">
      <div class="col-12 hr-body-medium hr-text-on-surface-variant">
        {{ $t('common.timeZone', { timeZone: currentIANATimezone }) }}
      </div>
      <div class="col-12">
        <template
          v-for="(weekday, weekdayIdx) of weekdays"
          :key="weekday.weekdayIndex"
        >
          <hr-divider v-if="weekdayIdx > 0" />
          <div class="row items-start q-col-gutter-md q-py-sm">
            <div class="col-auto availability-editor__day">
              <div class="availability-editor__line">
                <hr-on-off-switch
                  v-if="autosave"
                  :model-value="weekday.hasAvailability"
                  :label="weekday.name"
                  @update:model-value="onToggleAvailability(weekday)"
                />
                <hr-checkbox
                  v-else
                  :model-value="weekday.hasAvailability"
                  :label="weekday.name"
                  @update:model-value="onToggleAvailability(weekday)"
                />
              </div>
            </div>
            <div :class="weekday.hasAvailability ? 'col-12 col-sm' : 'col'">
              <template v-if="weekday.hasAvailability">
                <div
                  v-for="(availabilitySlot, availabilitySlotIdx) of weekday.availabilitySlots"
                  :key="availabilitySlot.id"
                  class="row items-center q-col-gutter-x-sm"
                >
                  <div class="availability-editor__slot">
                    <div class="row no-wrap items-center q-col-gutter-x-sm">
                      <div class="col">
                        <hr-time-range-field
                          :start-time="availabilitySlot.from"
                          :end-time="availabilitySlot.to"
                          :from-label="$t('common.labelFrom')"
                          :to-label="$t('common.labelTo')"
                          :start-error="availabilitySlot.hasConflict === true"
                          :end-error="availabilitySlot.hasZeroDuration === true"
                          :time-zone="currentIANATimezone"
                          dense
                          hide-bottom-space
                          @update:start-time="onSetAvailabilitySlotFrom(availabilitySlot, $event)"
                          @update:end-time="onSetAvailabilitySlotTo(availabilitySlot, $event)"
                        />
                      </div>
                      <div class="col-auto availability-editor__line availability-editor__slot-actions">
                        <hr-icon-action-btn
                          v-if="canDeleteAvailabilitySlot(weekday, availabilitySlot)"
                          icon="remove"
                          size="sm"
                          :aria-label="$t('availabilityEditor.tooltipRemoveTimeRange')"
                          @click="onDeleteAvailabilitySlot(availabilitySlot)"
                        >
                          <hr-tooltip>
                            {{ $t('availabilityEditor.tooltipRemoveTimeRange') }}
                          </hr-tooltip>
                        </hr-icon-action-btn>
                        <hr-icon-action-btn
                          v-if="availabilitySlotIdx === weekday.availabilitySlots.length - 1"
                          icon="add"
                          size="sm"
                          :aria-label="$t('availabilityEditor.tooltipAddTimeRange')"
                          :disable="disableAddAvailabilitySlot(weekday)"
                          @click="onAddAvailabilitySlot(weekday)"
                        >
                          <hr-tooltip>
                            {{ $t('availabilityEditor.tooltipAddTimeRange') }}
                          </hr-tooltip>
                        </hr-icon-action-btn>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="availabilitySlotIdx === weekday.availabilitySlots.length - 1 && weekday.weekdayIndex !== 6"
                    class="col-auto availability-editor__line"
                  >
                    <hr-subtle-btn
                      icon="content_copy"
                      :label="$t('availabilityEditor.labelCopyToNextDay', { day: getNextDayLabel(weekday) })"
                      @click="onCopyAvailabilitySlotToNext(weekday)"
                    />
                  </div>
                </div>
              </template>
              <div
                v-else
                class="hr-body-medium hr-text-on-surface-variant availability-editor__line
                  availability-editor__unavailable q-gutter-x-sm"
              >
                <hr-icon name="bedtime" />
                <span>{{ $t('common.labelUnavailable') }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
<script>
import { Timestamp } from 'firebase/firestore';
import { chain } from 'lodash';
import { uid } from 'quasar';
import { useTime } from 'src/composables/time';
import {
  confirmDestructiveDialog,
  HrCheckbox,
  HrDivider,
  HrIcon,
  HrIconActionBtn,
  HrOnOffSwitch,
  HrSettingSwitch,
  HrSubtleBtn,
  HrTimeRangeField,
  HrTooltip,
} from 'src/design-system';
import Toolbox from 'src/mixins/Toolbox';

export default {
  name: 'AvailabilityEditor',

  components: {
    HrCheckbox,
    HrDivider,
    HrIcon,
    HrIconActionBtn,
    HrOnOffSwitch,
    HrSettingSwitch,
    HrSubtleBtn,
    HrTimeRangeField,
    HrTooltip,
  },

  mixins: [Toolbox],

  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    enableLabel: {
      type: String,
      default: null,
    },
    disableConfirmationLabel: {
      type: String,
      default: null,
    },
    // Switches claim a change is already in force, which is only true where each edit is written as
    // it happens. Under a Save button the same claim would be false, so those get checkboxes.
    autosave: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const {
      deviceToTimezoneOffset,
      currentIANATimezone,
      getDay,
      adjustDate,
      getDate,
      getMinutes,
      subtractFromDate,
      addToDate,
      startOfDate,
      endOfDate,
      getHours,
    } = useTime();

    return {
      deviceToTimezoneOffset,
      currentIANATimezone,
      getDay,
      adjustDate,
      getDate,
      getMinutes,
      subtractFromDate,
      addToDate,
      startOfDate,
      endOfDate,
      getHours,
    };
  },

  data() {
    return {
      // The last slots emitted and not yet handed back. A start-time change emits twice in one tick
      // (start, then the end re-based on it), and the second edit must build on the first rather than
      // on a modelValue the parent has not passed down yet.
      pendingSlots: null,
    };
  },

  computed: {
    enableAvailability: {
      get() {
        return this.modelValue !== null;
      },
      set(value) {
        if (value) {
          this.$emit('update:modelValue', []);
        } else if (Array.isArray(this.modelValue) && this.modelValue.length === 0) {
          // No need to ask for permission if the availability slots are already empty
          this.$emit('update:modelValue', null);
        } else {
          confirmDestructiveDialog(
            this.$t('common.dialogTitleConfirm'),
            this.disableConfirmationLabel || this.$t('availabilityEditor.labelDisableConfirmation'),
            () => {
              this.$emit('update:modelValue', null);
            },
            { confirmLabel: this.$t('common.buttonClear') },
          );
        }
      },
    }, // enableAvailability()

    editAvailabilitySlots: {
      get() {
        return this.pendingSlots ?? this.modelValue ?? [];
      },
      set(value) {
        this.pendingSlots = value;
        this.$emit('update:modelValue', value);
      },
    },

    weekdays() {
      const weekdays = this.$tm('common.weekdays');

      let weekdayAvailability = weekdays.map((weekday, weekdayIndex) => {
        const availabilitySlots = this.editAvailabilitySlots.filter(
          (availabilitySlot) => availabilitySlot.isWeekday && weekdayIndex === availabilitySlot.weekdayIndex,
        );
        return {
          weekdayIndex,
          name: weekday,
          hasAvailability: availabilitySlots.length > 0,
          availabilitySlots,
        };
      });

      // Search for conflicts in availability slots
      weekdayAvailability = weekdayAvailability.map((weekday, weekdayIdx) => {
        const availabilitySlots = weekday.availabilitySlots.map((availabilitySlot, slotIdx) => {
          if (!(availabilitySlot.from instanceof Timestamp)) {
            // If either the current slot from-time is not set, skip the check
            return availabilitySlot;
          }
          const currentSlotFrom = availabilitySlot.from.toDate();

          let prevSlotTo;
          if (slotIdx > 0) {
            const prevSlot = weekday.availabilitySlots[slotIdx - 1];
            if (!(prevSlot?.to instanceof Timestamp)) {
              // If the previous slot to-time is not set, skip the check
              return availabilitySlot;
            }
            prevSlotTo = prevSlot.to.toDate();
          } else {
            // We are testing the first day of the week - make sure we "wrap around" to the previous week
            const prevSlot = weekdayIdx > 0
              ? weekdayAvailability[weekdayIdx - 1]?.availabilitySlots.slice(-1)[0]
              : weekdayAvailability[(weekdayIdx - 1 + 7) % 7]?.availabilitySlots.slice(-1)[0];
            if (!(prevSlot?.to instanceof Timestamp)) {
              // If the previous slot to-time is not set, skip the check
              return availabilitySlot;
            }
            prevSlotTo = (weekdayIdx > 0)
              ? prevSlot.to.toDate()
              : this.subtractFromDate(prevSlot.to.toDate(), { day: 7 });
          }
          if (currentSlotFrom <= prevSlotTo) {
            return {
              ...availabilitySlot,
              hasConflict: true,
            };
          }
          return availabilitySlot;
        });
        return {
          ...weekday,
          availabilitySlots,
        };
      });

      weekdayAvailability = weekdayAvailability.map((weekday) => ({
        ...weekday,
        availabilitySlots: weekday.availabilitySlots.map((slot) => {
          if (!(slot.from instanceof Timestamp) || !(slot.to instanceof Timestamp)) {
            return slot;
          }

          const fromDate = slot.from.toDate();
          const toDate = slot.to.toDate();
          if (this.getDay(fromDate) === this.getDay(toDate)
              && this.getHours(fromDate) === this.getHours(toDate)
              && this.getMinutes(fromDate) === this.getMinutes(toDate)) {
            return { ...slot, hasZeroDuration: true };
          }

          return slot;
        }),
      }));

      return weekdayAvailability;
    }, // weekdays()

    hasErrors() {
      return this.weekdays.some(
        (weekday) => weekday.availabilitySlots.some((slot) => slot.hasConflict || slot.hasZeroDuration),
      );
    }, // hasErrors()
  },

  watch: {
    modelValue() {
      this.pendingSlots = null;
    },
  },

  methods: {
    onToggleAvailability(weekday) {
      if (weekday.hasAvailability) {
        // Remove all availability slots for the selected weekday
        this.editAvailabilitySlots = this.editAvailabilitySlots
          .filter((availabilitySlot) => (
            !availabilitySlot.isWeekday
            || this.getDay(availabilitySlot.from) !== weekday.weekdayIndex));
        return;
      }

      const newDate = new Date();
      // Gets last Sunday in the user's local timezone and offsets it by week day
      const dayOfMonth = (this.getDate(newDate) - this.getDay(newDate)) + weekday.weekdayIndex;
      const from = Timestamp.fromDate(this.startOfDate(this.adjustDate(newDate, { day: dayOfMonth }), 'day'));
      const to = Timestamp.fromDate(this.endOfDate(this.adjustDate(newDate, { day: dayOfMonth }), 'day'));
      // // Set time to 00:00 of the selected weekday after 2000-01-02 in the user's local timezone
      // const from = Timestamp.fromDate(new Date(2000, 0, 2 + weekday.weekdayIndex, 0, 0, 0));
      // const to = Timestamp.fromDate(new Date(2000, 0, 2 + weekday.weekdayIndex, 23, 59, 59));

      const newAvailability = {
        id: uid(),
        from,
        to,
        isWeekday: true,
        weekdayIndex: weekday.weekdayIndex,
      };
      this.editAvailabilitySlots = [...this.editAvailabilitySlots, newAvailability];
    }, // onToggleAvailability()

    onSetAvailabilitySlotFrom(availabilitySlot, value) {
      this.editAvailabilitySlots = this.editAvailabilitySlots
        .map((slot) => ((slot.id === availabilitySlot.id)
          ? { ...slot, from: value, to: this.adjustToDate(slot.to, value) }
          : slot));
    }, // onSetAvailabilitySlotFrom()

    onSetAvailabilitySlotTo(availabilitySlot, value) {
      this.editAvailabilitySlots = this.editAvailabilitySlots
        .map((slot) => ((slot.id === availabilitySlot.id)
          ? { ...slot, to: this.adjustToDate(value, slot.from) }
          : slot));
    }, // onSetAvailabilitySlotTo()

    onAddAvailabilitySlot(weekday) {
      const from = chain(weekday.availabilitySlots)
        .map((slot) => (
          slot.to instanceof Timestamp
            ? this.adjustDate(slot.to, { second: 0, minute: this.getMinutes(slot.to) + 1 })
            : null))
        .max()
        .value();
      if (this.getDay(from) !== weekday.weekdayIndex) {
        return;
      }

      const newDate = new Date();
      // Gets last Sunday in the user's local timezone and offsets it by week day
      const dayOfMonth = (this.getDate(newDate) - this.getDay(newDate)) + weekday.weekdayIndex;

      const newAvailability = {
        id: uid(),
        from: Timestamp.fromDate(from),
        to: Timestamp.fromDate(this.endOfDate(this.adjustDate(newDate, { day: dayOfMonth }), 'day')),
        isWeekday: true,
        weekdayIndex: weekday.weekdayIndex,
      };
      this.editAvailabilitySlots = [...this.editAvailabilitySlots, newAvailability];
    }, // onAddAvailabilitySlot()

    onCopyAvailabilitySlotToNext(weekday) {
      const nextWeekdayIndex = weekday.weekdayIndex + 1;
      if (nextWeekdayIndex > 6) {
        return; // Do not copy to the next day if it is Sunday
      }
      this.editAvailabilitySlots = [
        // Exclude existing next day availability slots
        ...this.editAvailabilitySlots.filter((slot) => slot.weekdayIndex !== nextWeekdayIndex),
        // Copy the all availability slot of the current day to the next day
        ...weekday.availabilitySlots.map((slot) => ({
          ...slot,
          id: uid(),
          weekdayIndex: nextWeekdayIndex,
          from: Timestamp.fromDate(this.addToDate(slot.from.toDate(), { day: 1 })),
          to: Timestamp.fromDate(this.addToDate(slot.to.toDate(), { day: 1 })),
        })),
      ];
    }, // onCopyAvailabilitySlotToNext()

    onDeleteAvailabilitySlot(availabilitySlot) {
      this.editAvailabilitySlots = this.editAvailabilitySlots.filter((slot) => slot.id !== availabilitySlot.id);
    }, // onDeleteAvailabilitySlot()

    /**
     * Disable adding availability slot if the current slots exceed the selected weekday. It is
     * technically valid to have a slot that spans multiple days if they were originally set in another
     * timezone, but we want to keep it simple and avoid confusion.
     * @param weekday Weekday object with availabilitySlots
     */
    disableAddAvailabilitySlot(weekday) {
      return weekday.availabilitySlots
        .some((slot) => (
          slot.to instanceof Timestamp
            ? this.getDay(this.addToDate(slot.to, { minute: 1 })) !== weekday.weekdayIndex
            : true));
    }, // disableAddAvailabilitySlot()

    canDeleteAvailabilitySlot(weekday, availabilitySlot) {
      return (
        weekday.availabilitySlots.length > 1
        && weekday.availabilitySlots.findIndex((slot) => slot === availabilitySlot) !== 0
      );
    }, // canDeleteAvailabilitySlot()

    getNextDayLabel(weekday) {
      return this.$tm('common.weekdays')[(weekday.weekdayIndex + 1) % 7];
    }, // getNextDayLabel()
  },
};
</script>
<style lang="scss" scoped>
/*
 * Layout outside the design system, on purpose: the DS has no row that pairs a day control with a list
 * of time ranges, and these sizes only mean something for this editor's content. Color, type and the
 * touch target still come from the DS. A second screen needing this row shape makes it a DS component.
 */

/*
 * One line height for everything in a weekday row, so the day control, each time range and the
 * "Unavailable" text center on the same line. The touch target is the switch's height, the tallest of
 * them; what it leaves around a dense field doubles as the spacing between stacked ranges.
 */
.availability-editor__line {
  min-height: var(--app-comp-touch-target-size);
  display: flex;
  align-items: center;
}

/*
 * Fixed rather than a fraction of the row: a fraction shrinks below the switch and the longest
 * weekday name on a narrow screen, and the label runs into the time fields. Fixed also keeps every
 * day's ranges starting at the same x.
 */
@media (min-width: $breakpoint-sm-min) {
  .availability-editor__day {
    width: 12rem;
  }
}

/*
 * On a phone the ranges drop below the day, but "Unavailable" is short enough to stay beside it.
 * The day column is as wide as its name there, so it is right-aligned to line up down the list.
 */
@media (max-width: $breakpoint-xs-max) {
  .availability-editor__unavailable {
    justify-content: flex-end;
  }
}

/*
 * A range and its buttons wrap as one unit, and the range is what gives way on a narrow screen. The
 * basis caps it on a wide one: two time fields stretched across the page read as one long input.
 */
.availability-editor__slot {
  flex: 0 1 24rem;
  min-width: 0;
}

/* Room for both buttons on every range, even where one or neither shows, so a day's ranges share a width. */
.availability-editor__slot-actions {
  box-sizing: content-box;
  width: calc(2 * var(--app-comp-icon-btn-container-size-sm));
}
</style>
